// Persisted-Workout / history-entry -> Logger draft hydration. Pure
// (no React, no component state). Split out of workoutMapping.ts
// (Track R) along the source seam: this file owns saved-workout + copy-
// from-history hydration; plan-starter hydration lives in
// planStarterHydration.ts and draft->payload serialization in
// workoutPayload.ts. All three re-export through workoutMapping.ts.
//
// The per-set stored-WorkoutSet -> DraftSet shape construction (shared by
// hydrateFromWorkout's edit/repeat map and draftSetsFromHistoryEntry's
// copy) lives in storedSetToDraftSet.ts; every divergence between the two
// call sites rides on StoredSetToDraftOpts, locked by
// __tests__/workoutHydration.test.ts.
import {
  rirToRpe,
  rpeToRir,
  type MovementPattern,
  type NewWorkout,
  type Workout as WorkoutT,
  type WorkoutExerciseEntry,
} from '@heraion/shared';
import type {
  DraftEntry,
  DraftSet,
  LoggerDraft,
  WeightUnit,
} from './state/loggerDraftTypes';
import { storedSetToDraftSet } from './storedSetToDraftSet';
import { canonicalRirFromNumbers } from './state/canonicalRir';

// Converts a server-side WorkoutT (assembleWorkout output) into the
// Logger's draft shape. Only weighted + bodyweight kinds are supported
// in the MVP; the caller is expected to refuse opening edit mode on
// any workout containing other kinds (see canEditOnMobile below).
//
// Display unit is required (the caller's preference); the user can flip per-row via the
// chip if they prefer lb. Stored canonical kg round-trips losslessly
// regardless of display.
export function hydrateFromWorkout(
  w: WorkoutT,
  unit: WeightUnit,
  patternsBySlug: Record<string, MovementPattern | null> = {},
  mode: 'edit' | 'repeat' = 'edit',
): LoggerDraft {
  const entries: DraftEntry[] = w.entries.map((e) => {
    const isBodyweight = e.sets[0]?.type === 'bodyweight';
    const isCardio = e.sets[0]?.type === 'cardio';
    const isTimeBased = e.sets[0]?.type === 'timed';
    return {
      exerciseId: e.exerciseId,
      exerciseName: e.exerciseName ?? e.exerciseId,
      isBodyweight,
      isCardio,
      isTimeBased,
      movementPattern: patternsBySlug[e.exerciseId] ?? null,
      // Group membership (superset/circuit) is STRUCTURE → carries on BOTH
      // edit and repeat. Repeating a superset workout must keep the
      // supersets (bug 2026-05-28: repeat dropped them, "si perde i
      // superset"). The old drop-on-repeat mirrored web's
      // entriesFromWorkout(w, false), but web is behind here; mobile keeps
      // the grouping so the repeated session is the same shape.
      group: e.group ?? null,
      // EQ-1: the machine and the place are STRUCTURE, exactly like `group`
      // above, so they carry on BOTH edit and repeat. Repeating a session
      // must stay on the same machine — dropping them would silently re-file
      // the repeated work under "no instance" and split the very history
      // they exist to keep together.
      equipmentInstance: e.equipmentInstance ?? null,
      location: e.location ?? null,
      // Not structure: a snapshot of the saved session, so edit only (#38).
      ...(mode === 'edit' && e.tags?.length ? { tags: e.tags } : {}),
      // Per-exercise note retired (note-model consolidation): the instruction
      // is display-only, re-resolved per-slug from the plan by usePlanCues on
      // edit/repeat, never rehydrated from the saved entry.
      // Effort column ON by default except cardio + timed holds (bodyweight
      // included) so an edit/repeat surfaces the saved rpe/rir. Scale follows
      // Profile.effortScale at render.
      effortColumn: !isCardio && !isTimeBased,
      // HistoryPanel collapsed by default on every hydration path.
      historyOpen: false,
      // Entry-level default seeded from the LAST weighted set's
      // loadedSides so editing a unilateral session keeps the toggle
      // in unilateral mode for any add_set after hydrate. Falls back
      // to 1 (bilateral) when no weighted set carried the field — the
      // entered weight is the total load, so the multiplier is 1 unless
      // the exercise is explicitly unilateral.
      loadedSides: (() => {
        for (let i = e.sets.length - 1; i >= 0; i--) {
          const s = e.sets[i];
          if (
            (s?.type === 'weighted' || s?.type === 'bodyweight') &&
            (s.loadedSides === 1 || s.loadedSides === 2)
          ) {
            return s.loadedSides;
          }
        }
        return 1 as const;
      })(),
      sets: e.sets.map((s, setIdx): DraftSet => {
        // Repeat mode clones structure not history: the new draft starts
        // as still-to-do, so completedAt / fail / per-set note are blanked.
        // Edit mode preserves them so the user sees the session's state.
        // RPE/RIR carry on BOTH (Antenore 2026-05-28): on repeat the prior
        // effort is the baseline to compare against / the prescribed RIR.
        const completedAt =
          mode === 'repeat'
            ? null
            : typeof s.completedAt === 'string' && s.completedAt.length > 0
              ? s.completedAt
              : null;
        const fail = mode === 'repeat' ? false : 'fail' in s && s.fail === true;
        // AMRAP rides with `fail`, not with the structure: it records that
        // THIS performance went to the limit on the open variable. Edit must
        // hand it back or the re-save would silently erase a flag the athlete
        // set (the same wipe weightSourceRoundTrip guards against); repeat
        // starts clean, since the cloned set hasn't been taken to the limit
        // yet. Legacy rows have no field → false, i.e. non-AMRAP.
        const amrap =
          mode === 'repeat' ? false : 'amrap' in s && s.amrap === true;
        // The header set's note comes back as `e.notes` (entryHeaderSet). Edit
        // returns it to the earliest set, where the split writes the header,
        // or a correction would drop it (#38).
        const note =
          mode === 'repeat'
            ? ''
            : 'note' in s && typeof s.note === 'string'
              ? s.note
              : setIdx === 0
                ? (e.notes ?? '')
                : '';
        // One canonical stored (rir, AP-0a); the draft carries BOTH text
        // fields because the effort input binds one per Profile.effortScale.
        // Project the missing scale here so a rir-only set still shows a
        // value when viewed in RPE mode (and vice versa for legacy
        // rpe-only rows). Stored values win over projections.
        const rpeNum = 'rpe' in s && typeof s.rpe === 'number' ? s.rpe : null;
        const rirNum = 'rir' in s && typeof s.rir === 'number' ? s.rir : null;
        // ⭐ A REPEAT carries the previous effort as a TARGET, not as a
        // measurement (Antenore, 2026-08-25): re-running a session proposes
        // what was done before, it does not assert it was done again. So the
        // value crosses to the PRESCRIPTION half and the outcome starts
        // empty — the same border 'fail'/'amrap'/completedAt already keep
        // just above. An EDIT is the opposite case: there these ARE the
        // athlete's judgment, and they stay exactly where they are.
        const canonicalRir = canonicalRirFromNumbers(
          rirNum ?? undefined,
          rpeNum ?? undefined,
        );
        // ⛔ Nessun gate su `weightSource` qui, al contrario di
        // planStarterHydration — deliberato, non dimenticato (rialzato da una
        // review il 26/08). Un repeat non ha prescrizioni: ha l'ECO della
        // sessione scorsa, e sua parola dello stesso giorno e' che quell'eco
        // diventa il bersaglio. Il weightSource che il clone eredita dice da
        // dove veniva il peso L'ALTRA VOLTA, non che oggi il carico sia l'asse.
        // Pinnato in repeatHydration.test.ts.
        const rirPrescribed =
          mode === 'repeat' && canonicalRir !== null ? canonicalRir : undefined;
        const rpe =
          mode === 'repeat'
            ? ''
            : rpeNum !== null
              ? String(rpeNum)
              : rirNum !== null
                ? String(rirToRpe(rirNum))
                : '';
        const rir =
          mode === 'repeat'
            ? ''
            : rirNum !== null
              ? String(rirNum)
              : rpeNum !== null
                ? String(rpeToRir(rpeNum))
                : '';
        return storedSetToDraftSet(s, {
          unit,
          completedAt,
          fail,
          amrap,
          note,
          rpe,
          rir,
          rirPrescribed,
          weightedLoadedSidesFallback: 1,
          includeDropset: true,
          includeWeightSource: true,
          includeCardioOutcomes: true,
        });
      }),
    };
  });
  // Repeat mode resets the start clock so the cloned draft reads as
  // "you are starting NOW", not as a back-dated copy of yesterday's
  // session. Edit keeps the original startedAt verbatim.
  const startedAt = mode === 'repeat' ? new Date().toISOString() : w.startedAt;
  // endedAt only hydrates in edit mode: repeating clones structure but
  // not history, so the new session is "still in progress" until the
  // user saves. The footer date editor reads draft.endedAt to render
  // the End-time field on the same beat the web does.
  const endedAt = mode === 'edit' ? w.endedAt : undefined;
  // Carry the label forward on both edit and repeat — repeating a
  // workout is usually "same session shape" so "Push A" stays "Push A"
  // (mirrors web's repeatDraftFromWorkout); editing must preserve the
  // existing name or save would blank it.
  // Coach-notes reference: carry the source plan/program id on BOTH edit and
  // repeat so the workout-level "?" panel (program description + day note)
  // re-appears for a past session re-opened from history — the bug Antenore
  // caught 2026-06-06 ("le note non si vedono nel repeat"). This is the
  // DISPLAY-only reference (coachNotesRef), NOT the save `fromPlanId`: a
  // repeat must not re-flip the source plan / re-advance the program, so the
  // reference never reaches the payload. useCoachNotes resolves the notes
  // from the Plan-tab's cached programs/plans against it (offline-safe).
  // EQ-1 slice D — the session's place, recovered from the entries.
  //
  // Without this, reopening a session in Edit or Repeat showed "Where?" even
  // though every entry already carried a gym, and an exercise added mid-repeat
  // did not inherit it — one re-tap on the commonest flow there is (repeat the
  // usual day at the usual gym, add a set). The entries ARE the record, so the
  // session-level value is derived from them rather than stored twice.
  //
  // The most common value wins, not the first: a session that moved keeps the
  // place most of its work happened at, and ties fall to the earlier entry.
  const location = mostCommonLocation(entries);

  return {
    startedAt,
    entries,
    ...(endedAt ? { endedAt } : {}),
    ...(location ? { location } : {}),
    ...(w.name ? { name: w.name } : {}),
    ...(w.fromPlanId ? { coachNotesRef: w.fromPlanId } : {}),
  };
}

function mostCommonLocation(
  entries: readonly { location: string | null }[],
): string | null {
  const counts = new Map<string, number>();
  for (const e of entries) {
    if (!e.location) continue;
    counts.set(e.location, (counts.get(e.location) ?? 0) + 1);
  }
  let best: string | null = null;
  let bestN = 0;
  // Insertion order is entry order, so a tie leaves `best` on the earlier one.
  for (const [value, n] of counts) {
    if (n > bestN) {
      best = value;
      bestN = n;
    }
  }
  return best;
}

export function canEditOnMobile(w: WorkoutT): boolean {
  for (const e of w.entries) {
    for (const s of e.sets) {
      if (
        s.type !== 'weighted' &&
        s.type !== 'bodyweight' &&
        s.type !== 'cardio' &&
        s.type !== 'timed'
      ) {
        return false;
      }
    }
  }
  return true;
}

// Convert a saved session's exercise entry into draft-shape sets in the
// blank state (no completedAt, no fail, no notes/rpe — the lifter is
// about to perform them, not review them). Drops any kind beyond the
// supported four to match canEditOnMobile. Shares the per-kind shape
// construction with hydrateFromWorkout via storedSetToDraftSet; the
// blank-slate opts express the divergence (cardio HR/calories blanked,
// no dropset/weightSource carry, loadedSides falls back to the entry's
// current default).
export function draftSetsFromHistoryEntry(
  src: WorkoutExerciseEntry,
  unit: WeightUnit,
  entryLoadedSides: 1 | 2,
): DraftSet[] {
  const out: DraftSet[] = [];
  for (const s of src.sets) {
    if (
      s.type !== 'weighted' &&
      s.type !== 'bodyweight' &&
      s.type !== 'cardio' &&
      s.type !== 'timed'
    ) {
      continue;
    }
    out.push(
      storedSetToDraftSet(s, {
        unit,
        completedAt: null,
        fail: false,
        // Blank slate: you can't copy "I went to failure" onto a set you
        // are about to perform — same rule as fail/completedAt above.
        amrap: false,
        note: '',
        rpe: '',
        rir: '',
        weightedLoadedSidesFallback: entryLoadedSides,
        includeDropset: false,
        includeWeightSource: false,
        includeCardioOutcomes: false,
      }),
    );
  }
  return out;
}

// "Fresh" entry: never touched. No completion stamps and no typed
// values. The HistoryPanel copy action skips the overwrite/append
// popup in this state — there's nothing meaningful to preserve, so
// the copy just drops onto the empty placeholder.
export function isEntryFresh(entry: DraftEntry): boolean {
  return entry.sets.every(
    (s) =>
      s.completedAt === null &&
      !s.fail &&
      s.reps.trim() === '' &&
      s.weight.trim() === '',
  );
}

/** A queued correction whose session was deleted on another device, as a
 *  draft the recovery card offers to save as a new workout (#38 M). Same
 *  path as an edit, so completed sets, effort and notes come back as saved. */
export function draftFromQueuedWorkout(
  w: NewWorkout,
  unit: WeightUnit,
): LoggerDraft {
  const at = new Date().toISOString();
  return hydrateFromWorkout(
    { ...w, id: 'recovered', profileId: '', createdAt: at, updatedAt: at },
    unit,
    {},
    'edit',
  );
}
